import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';

// Post-Stripe success landing for COPPA VPC.
// Reads session_id from the query string, confirms with backend, backend
// captures the payment_intent + refunds $0.01, sets coppa_parent_verified=true.
export default function CoppaVerifySuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | verified | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setStatus('error');
      setMessage('Missing session_id in URL.');
      return;
    }
    (async () => {
      try {
        const r = await api.post('/apex/coppa/verify-confirm', { session_id: sessionId });
        if (r.data.verified) {
          setStatus('verified');
          setMessage(r.data.message || 'Verified.');
        } else {
          setStatus('error');
          setMessage('Verification did not complete.');
        }
      } catch (e) {
        setStatus('error');
        setMessage(e?.response?.data?.detail || 'Verification failed.');
      }
    })();
  }, [params]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-6" data-testid="coppa-success-page">
      <Card className={`max-w-md w-full border-2 ${
        status === 'verified' ? 'border-green-500/60 bg-green-500/5' :
        status === 'error' ? 'border-red-500/40 bg-red-500/5' :
        'border-gray-700 bg-[#111]'
      }`}>
        <CardContent className="p-6 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-10 h-10 text-orange-400 animate-spin mx-auto mb-3" />
              <div className="font-bold text-lg mb-1" data-testid="coppa-verifying">Verifying with Stripe…</div>
              <p className="text-sm text-gray-400">Do not close this tab.</p>
            </>
          )}
          {status === 'verified' && (
            <>
              <ShieldCheck className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <div className="font-black text-2xl mb-2 text-green-300" data-testid="coppa-verified">Consent Verified</div>
              <p className="text-sm text-gray-300 mb-4">{message}</p>
              <Button onClick={() => navigate('/apex/course')}
                      className="bg-green-500 hover:bg-green-600 text-black font-bold"
                      data-testid="coppa-continue-btn">
                Continue to A.P.E.X.
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <div className="font-black text-xl mb-2" data-testid="coppa-error-message">Verification Failed</div>
              <p className="text-sm text-gray-300 mb-4">{message}</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => navigate('/apex/coppa/verify')} variant="outline"
                        className="border-gray-700 text-white">Try Again</Button>
                <Button onClick={() => navigate('/apex/course')} variant="outline"
                        className="border-gray-700 text-white">Back to Course</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
